import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type RefreshFn = () => void | Promise<void>;

type PortalRefreshContextValue = {
  registerRefresh: (fn: RefreshFn | null) => void;
  refresh: () => void;
  hasRefresh: boolean;
};

const PortalRefreshContext = createContext<PortalRefreshContextValue | null>(null);

export function PortalRefreshProvider({ children }: { children: ReactNode }) {
  const handlerRef = useRef<RefreshFn | null>(null);
  const [hasRefresh, setHasRefresh] = useState(false);

  const registerRefresh = useCallback((fn: RefreshFn | null) => {
    handlerRef.current = fn;
    queueMicrotask(() => {
      setHasRefresh((prev) => {
        const next = fn != null;
        return prev === next ? prev : next;
      });
    });
  }, []);

  const refresh = useCallback(() => {
    void handlerRef.current?.();
  }, []);

  const value = useMemo(
    () => ({
      registerRefresh,
      refresh,
      hasRefresh,
    }),
    [registerRefresh, refresh, hasRefresh],
  );

  return <PortalRefreshContext.Provider value={value}>{children}</PortalRefreshContext.Provider>;
}

export function usePortalRefresh() {
  const ctx = useContext(PortalRefreshContext);
  if (!ctx) {
    throw new Error('usePortalRefresh doit être utilisé dans PortalRefreshProvider');
  }
  return ctx;
}

/** Enregistre la fonction d’actualisation de la page courante (barre supérieure). */
export function useRegisterPortalRefresh(refreshFn: RefreshFn | null | undefined) {
  const { registerRefresh } = usePortalRefresh();
  const fnRef = useRef(refreshFn);
  fnRef.current = refreshFn;

  useEffect(() => {
    registerRefresh(() => {
      void fnRef.current?.();
    });
    return () => registerRefresh(null);
  }, [registerRefresh]);
}
